import { Component, HostListener, Inject, OnInit } from '@angular/core';
import {
  DwComponent, DwComponentType, DwExpressionService, DwFormContextMode, DwMdFormContextInfo, DwMetaDataFormApi, DwMetaDataFormStateService,
  DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken,
  DwLookupSearchType, DwOrmDataService, DwOrmDataServiceToken, DwQueryHelper, DwQueryFilter, DwOperator, DwFilterType,
  isAfterDate
}
  from '@devwareapps/devware-cap';
import { AppMetaDataItemNames, CourseLessonEntity, CourseLessonProgressEntity, CourseLessonSessionEntity, DocumentTypeAllItems, LessonTypeAllItems, QuizResultEntity, VideoEntity } from '../../../../meta-data/app-meta-data.service';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { CourseNavigationService } from '../../services/course-navigation.service';
import { LessonNavigationInfo } from '../../models/lesson-navigation-info.model';
import { LessonProgressHierarchy } from '../../models/lesson-progress-hierarchy.model';
import { DateTimeUtilService } from '../../../shared/util/date-time-util.service';
import { VideoEvent } from '../../../videos/models/video-event.model';
import { VideoEventType } from '../../../videos/models/video-event-type.enum';
import { QuizProgressEvent } from '../../../quizzes/models/quiz-progress-event.model';
import { LessonFlagUtilService } from '../../services/lesson-flag-util.service';
import { QuizHomeView } from '../../../quizzes/models/quiz-home-view.enum';
import { DocumentProgressEvent } from '../../../documents/models/document-progress-event.model';
import { LessonProgressItem } from '../../models/course-progress-hierarchy.model';
import { finalize } from 'rxjs/operators';
import { LessonVideoPlayback } from '../../../videos/models/video-playback.model';
import { ModuleVideoPlaybackService } from '../../../videos/services/module-video-playback.service';


@DwComponent({
  key: 'lesson-ui',
  name: 'Lesson UI',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.CourseLesson,

})
@Component({
  selector: 'app-lesson-ui',
  templateUrl: './lesson-ui.component.html',
  styleUrls: ['./lesson-ui.component.scss']
})
/** @deprecated - this component was the original "Lesson Detail View" and has been renamed and repurposed as part of the lesson view redesign. The new LessonDetailViewComponent is now the main component for rendering lessons, and this LessonUiComponent is only used for rendering the "Lesson Overview" tab content within the new design. Once the overview tab is fully implemented and any remaining unique functionality in this component is migrated over, this component should be deleted. */
export class LessonUiComponent extends DwSectionBaseComponent {
  lesson: CourseLessonEntity;
  video: VideoEntity;
  formApi: DwMetaDataFormApi;
  lessonNavigationInfo: LessonNavigationInfo;
  progress: LessonProgressHierarchy;
  videoPlayback: LessonVideoPlayback = null;

  videoLessonType = LessonTypeAllItems.Video;
  quizLessonType = LessonTypeAllItems.Quiz;
  documentLessonType = LessonTypeAllItems.ProTools;
  currentUpdateTimeSeconds: number;
  currentTimeToAddSeconds: number;
  pageSessionStart: string;
  lastVideoStart: string;
  private lastVideoProgressSaveDateTime: string;
  private lastSavedFurthestVideoTime: number = 0;
  private pendingVideoProgressUpdate: { furtherestVideoTime: number; percentComplete: number; event?: VideoEvent } | null = null;
  private currentLessonSessionKey: string;
  private videoSessionStarted: boolean = false;
  private videoPlaybackProgressObserved: boolean = false;
  private videoCompletionSaveStarted: boolean = false;
  private videoCompletionSaved: boolean = false;
  private readonly videoProgressAutosaveIntervalSeconds = 30;
  private readonly videoProgressAutosaveDeltaSeconds = 15;
  private readonly maxVideoPlaybackDeltaSeconds = 15;
  private readonly videoSessionInactivitySplitSeconds = 90;
  private observedVideoPlaybackSeconds = 0;
  private lastObservedVideoTimeSeconds: number = null;
  private lastObservedPlaybackDateTime: string = null;
  videoWidth: string;
  flagEnabled: boolean = false;
  flagSubmitted: boolean;
  lessonLocked: boolean = false;

  hideNavigation: boolean = true;

  // Lesson-view redesign: neighbour lessons (for the redesigned Previous /
  // Up-next footer cards) and a derived "has description" flag used to gate
  // the empty-state placeholder in the Aviator Intel overview.
  prevLessonItem: LessonProgressItem | null = null;
  nextLessonItem: LessonProgressItem | null = null;
  hasLessonDescription: boolean = false;

  // ──────────────────────────────────────────────────────────────────
  // MOCKUP DATA — placeholder content rendered on every lesson so the
  // designer can see the full layout (rich overview, Learning Objectives
  // card, multi-resource list) without real content authored in the LMS.
  // Delete this block and the `useMockLessonData` bindings in the template
  // once real fields (objectives collection, multi-document relation) are
  // wired up on CourseLessonEntity.
  // ──────────────────────────────────────────────────────────────────
  useMockLessonData: boolean = false;

  mockResources: { name: string; type: string; size: string }[] = [
    { name: 'Stability — Lesson Notes.pdf', type: 'PDF', size: '1.2 MB' },
    { name: 'Three Axes Diagram.pdf', type: 'PDF', size: '640 KB' },
    { name: 'FAA PHAK Ch. 5 — Aerodynamics.pdf', type: 'PDF', size: '4.8 MB' },
    { name: 'CG & Stability Quick Reference.pdf', type: 'CHART', size: '320 KB' },
  ];

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private courseRepositoryService: CourseRepositoryService,
    private courseNavigationService: CourseNavigationService,
    private dateTimeUtilService: DateTimeUtilService,
    private lessonFlagUtilService: LessonFlagUtilService,
    private moduleVideoPlaybackService: ModuleVideoPlaybackService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    this.setupLesson();
  }

  quizViewChanged(view: QuizHomeView) {
    switch (view) {
      case QuizHomeView.quiz:
      case QuizHomeView.quizAnswers:
        this.hideNavigation = true;
        break;
      default:
        this.hideNavigation = false;
        break;
    }
  }

  protected setupLesson(): void {
    this.flushVideoProgress(true);
    this.videoPlayback = null;

    const lessonEntity = this.formApi.getFormData<CourseLessonEntity>();

    const courseParameters = this.courseNavigationService.getCurrentCourseParameters();

    this.courseRepositoryService.getLessonProgress(courseParameters)
      .subscribe(lessonProgress => {
        this.progress = lessonProgress;

        //console.log('Lesson Progress', lessonProgress);

        let allowLessonLock = true;
        switch (lessonEntity?.LessonTypeId) {
          case LessonTypeAllItems.Video:
            allowLessonLock = false;
        }

        const studentCourse = lessonProgress?.course?.studentCourse;
        const writtenUploadedAndPassed = studentCourse?.AcsTestUploaded === true && studentCourse?.AcsTestPassed === true;
        const isSketchfabLesson = lessonProgress?.lesson?.lesson?.LessonTypeId === LessonTypeAllItems.ProTools
          && lessonProgress?.lesson?.lesson?.DocumentItem?.DocumentTypeId === DocumentTypeAllItems.SketchFab3D;
        this.lessonLocked = allowLessonLock && !(isSketchfabLesson && writtenUploadedAndPassed)
          && isAfterDate(studentCourse?.CourseEndorsementStudent?.ExpirationDateTime);

        this.lesson = this.progress.lesson.lesson;

        if (this.lesson?.LessonTypeId === LessonTypeAllItems.Video) {
          const studentCourseId = this.progress?.course?.studentCourse?.StudentCourseId;
          const courseModuleId = this.progress?.module?.module?.CourseModuleId;
          const courseLessonId = this.lesson?.CourseLessonId;
          if (studentCourseId && courseModuleId && courseLessonId) {
            this.moduleVideoPlaybackService.getLessonPlayback(studentCourseId, courseModuleId, courseLessonId)
              .subscribe(playback => {
                if (this.lesson?.CourseLessonId === playback?.LessonId) {
                  this.videoPlayback = {
                    ...playback,
                    LessonCompleted: playback.LessonCompleted === true
                      || this.progress?.lesson?.lessonProgress?.LessonComplete === true
                  };
                }
              });
          }
        }

        this.setupLessonDetails()

        this.setupLessonSession();

        this.computeLessonViewState();

        this.hideNavigation = false;
      });

    this.courseNavigationService.getLessonNavigationInfo(courseParameters)
      .subscribe(lessonNavigationInfo => {
        this.lessonNavigationInfo = lessonNavigationInfo;
      });
  }

  flagLesson() {
    if (this.flagSubmitted) {
      return;
    }

    this.lessonFlagUtilService.showLessonFlagModal(this.progress.course.course.CourseId, this.lesson.CourseLessonId)
      .subscribe(result => {
        if (!result.cancelled) {
          this.flagSubmitted = true;
        }
      });
  }

  setupLessonDetails() {
    switch (this.lesson.LessonTypeId) {
      case LessonTypeAllItems.Video:
        this.setupVideoLesson();
        break;
      case LessonTypeAllItems.Quiz:
        this.setupQuizLesson();
        break;
    }
  }

  videoWidthChanged(width: string) {
    this.videoWidth = width;

    //this.hideNavigation = false;
  }

  documentWidthChanged(width: string) {
    this.videoWidth = width;
  }

  setupQuizLesson() {

  }

  private setupVideoLesson() {
    //this.video = this.progress?.lesson?.lesson?.Video;
  }

  // Walk the course → module → lesson hierarchy, find the current lesson by
  // CourseLessonId, and grab its neighbours for the redesigned footer cards.
  // Returning null when there's no previous/next lets the template render the
  // "This is the first/last lesson." placeholder.
  private computeLessonViewState() {
    const currentId = this.lesson?.CourseLessonId;
    const flatLessons: LessonProgressItem[] = (this.progress?.course?.modules || [])
      .reduce<LessonProgressItem[]>((acc, m) => acc.concat(m?.lessons || []), []);

    const idx = flatLessons.findIndex(l => l?.lesson?.CourseLessonId === currentId);
    this.prevLessonItem = idx > 0 ? flatLessons[idx - 1] : null;
    this.nextLessonItem = idx >= 0 && idx < flatLessons.length - 1 ? flatLessons[idx + 1] : null;

    const desc = this.lesson?.LessonDescription;
    this.hasLessonDescription = !!(desc && desc.replace(/<[^>]*>/g, '').trim().length > 0);
  }

  navigateToPreviousLesson() {
    if (!this.lessonNavigationInfo?.previousLesson) return;
    this.courseNavigationService.navigateToLesson(this.lessonNavigationInfo.previousLesson);
  }

  navigateToNextLesson() {
    if (!this.lessonNavigationInfo?.nextLesson) return;
    if (this.lessonNavigationInfo.isLastLesson) {
      this.courseNavigationService.navigateToModule(
        this.lessonNavigationInfo.course?.CourseId,
        this.lessonNavigationInfo.nextLesson?.moduleId
      );
      return;
    }
    this.courseNavigationService.navigateToLesson(this.lessonNavigationInfo.nextLesson);
  }



  videoEvent(videoEvent: VideoEvent) {

    switch (videoEvent.videoEventType) {
      case VideoEventType.videoStopped:
      case VideoEventType.videoCompleted:
        this.setVideoProgress(videoEvent.furtherestVideoTime, videoEvent.percentComplete, videoEvent);
        break;
      case VideoEventType.videoPaused:
        this.setVideoProgress(videoEvent.furtherestVideoTime, videoEvent.percentComplete, videoEvent);
        break;
      case VideoEventType.videoProgress:
        this.handleVideoProgress(videoEvent);
        break;
      case VideoEventType.videoStarted:
        this.videoSessionStarted = true;
        this.lastVideoStart = this.dateTimeUtilService.getCurrentDateTime();
        if (!this.progress.currentLessonSession.SessionStartDateTime) {
          this.progress.currentLessonSession.SessionStartDateTime = this.lastVideoStart;
        }
    }
  }

  @HostListener('window:pagehide')
  onPageHide() {
    this.flushVideoProgress();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.flushVideoProgress();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.flushVideoProgress();
    }
  }

  documentSessionMaxTimeSeconds = 2 * 60; // 2 minutes

  documentEvent(documentEvent: DocumentProgressEvent) {
    const currentLessonSession = this.progress.currentLessonSession;
    currentLessonSession.LessonCompleted = documentEvent.isComplete;
    currentLessonSession.SessionStartDateTime = this.pageSessionStart;
    currentLessonSession.SessionEndDateTime = this.dateTimeUtilService.getCurrentDateTime();
    this.updateSessionTime();

    if (currentLessonSession.TotalTimeSeconds >= this.documentSessionMaxTimeSeconds) {
      currentLessonSession.TotalTimeSeconds = this.documentSessionMaxTimeSeconds;
    }

    const lessonProgress: Partial<CourseLessonProgressEntity> = {};

    lessonProgress.LastActivityDateTime = currentLessonSession.SessionEndDateTime;

    this.courseRepositoryService.updateLessonProgress(this.progress, lessonProgress, this.currentTimeToAddSeconds, false)
      .subscribe(result => {
        this.progress = result;

        //this.setupLessonSession();
      });
  }

  quizEvent(quizEvent: QuizProgressEvent) {
    const currentLessonSession = this.progress.currentLessonSession;
    currentLessonSession.LessonCompleted = quizEvent.isComplete;
    currentLessonSession.QuizResultId = quizEvent.quizResult?.QuizResultId;

    currentLessonSession.SessionStartDateTime = quizEvent.quizResult?.QuizStartDateTime;
    currentLessonSession.SessionEndDateTime = quizEvent.quizResult?.QuizStopDateTime;

    this.updateSessionTime(quizEvent.quizResult?.TotalTimeSeconds);

    const lessonProgress: Partial<CourseLessonProgressEntity> = {};

    // Don't update this since it will be handled on the server and trigger that a lesson was completed
    // if (quizEvent.isComplete) {
    // lessonProgress.LessonComplete = true;
    //   lessonProgress.PercentComplete = 100;
    // } 

    lessonProgress.BestQuizResultId = quizEvent.bestQuizResultId;
    lessonProgress.LastActivityDateTime = quizEvent.quizResult?.QuizStopDateTime;

    const forceReload = quizEvent.quizResult.ProgressReset;

    this.courseRepositoryService.updateLessonProgress(this.progress, lessonProgress, this.currentTimeToAddSeconds, forceReload)
      .subscribe(result => {
        this.progress = result;

        this.setupLessonSession();
      });
  }

  protected setupLessonSession() {
    this.pageSessionStart = this.dateTimeUtilService.getCurrentDateTime()
    this.lastVideoProgressSaveDateTime = null;
    this.lastSavedFurthestVideoTime = this.progress?.lesson?.lessonProgress?.VideoFurthestTimeSeconds || 0;
    this.pendingVideoProgressUpdate = null;
    this.currentLessonSessionKey = this.getLessonSessionKey();
    this.videoSessionStarted = false;
    this.videoPlaybackProgressObserved = false;
    this.videoCompletionSaveStarted = false;
    this.videoCompletionSaved = false;
    this.observedVideoPlaybackSeconds = 0;
    this.lastObservedVideoTimeSeconds = null;
    this.lastObservedPlaybackDateTime = null;
    this.lastVideoStart = null;
    this.currentTimeToAddSeconds = 0;
    this.updatingProgress = false;

    this.progress.currentLessonSession = {
      SessionStartDateTime: null,
      SessionEndDateTime: null,
      TotalTimeSeconds: 0,

      VideoId: this.lesson?.Video?.VideoId,
    }
  }

  markLessonComplete() {
    this.progress.currentLessonSession.LessonCompleted = true;

    const dateTime = this.dateTimeUtilService.getCurrentDateTime();

    if (!this.progress.currentLessonSession.SessionStartDateTime) {
      this.progress.currentLessonSession.SessionStartDateTime = this.pageSessionStart;
    }

    this.progress.currentLessonSession.SessionEndDateTime = dateTime;

    this.updateSessionTime();

    this.courseRepositoryService.updateLessonProgress(this.progress, { LessonComplete: true, LastActivityDateTime: dateTime }, this.currentTimeToAddSeconds, false)
      .subscribe(result => {
        this.progress = result;
      })
  }

  updatingProgress = false;
  setVideoProgress(furtherestVideoTime: number, percentComplete: number, event?: VideoEvent) {
    furtherestVideoTime = furtherestVideoTime || 0;
    percentComplete = percentComplete || 0;
    const isCompletion = percentComplete >= 100 || event?.videoEventType === VideoEventType.videoCompleted;

    if (isCompletion) {
      percentComplete = 100;
    }

    if (!this.videoSessionStarted && !furtherestVideoTime && percentComplete < 100) {
      return;
    }

    if (this.updatingProgress) {
      const pendingIsCompletion = this.pendingVideoProgressUpdate?.percentComplete >= 100
        || this.pendingVideoProgressUpdate?.event?.videoEventType === VideoEventType.videoCompleted;

      if (pendingIsCompletion && !isCompletion) {
        return;
      }

      if (isCompletion && (this.videoCompletionSaveStarted || this.videoCompletionSaved)) {
        return;
      }

      //console.log('Video Progress - Skipping Update', furtherestVideoTime, percentComplete, event);
      this.pendingVideoProgressUpdate = { furtherestVideoTime, percentComplete, event };
      return;
    }

    if (isCompletion) {
      if (this.videoCompletionSaveStarted || this.videoCompletionSaved) {
        return;
      }

      this.videoCompletionSaveStarted = true;
    }

    const lessonProgress: Partial<CourseLessonProgressEntity> = {};

    this.progress.currentLessonSession.LessonCompleted = (percentComplete >= 100);
    this.progress.currentLessonSession.VideoFurthestTimeSeconds = furtherestVideoTime;

    if (event?.videoEventType !== VideoEventType.videoProgress) {
      this.recordVideoPlaybackProgress(event);
    }

    //console.log('Video Progress - Updating', furtherestVideoTime, percentComplete, event);

    if (!lessonProgress.VideoFurthestTimeSeconds || lessonProgress.VideoFurthestTimeSeconds < furtherestVideoTime) {
      lessonProgress.VideoFurthestTimeSeconds = furtherestVideoTime;
    }
    const dateTime = this.dateTimeUtilService.getCurrentDateTime();

    if (!this.lastVideoStart) {
      this.lastVideoStart = this.progress.currentLessonSession.SessionStartDateTime || dateTime;
    }

    //lessonProgress.LastActivityDateTime = dateTime;
    this.progress.currentLessonSession.SessionEndDateTime = dateTime;

    let previousSeconds = this.progress.currentLessonSession.TotalTimeSeconds || 0;

    let newTotalTimeSeconds = Math.max(previousSeconds, this.observedVideoPlaybackSeconds);

    this.updateSessionTime(newTotalTimeSeconds);

    if (!this.hasVideoProgressChangesToSave(furtherestVideoTime, percentComplete)) {
      return;
    }

    this.updatingProgress = true;
    const saveSessionKey = this.currentLessonSessionKey;

    this.courseRepositoryService.updateLessonProgress(this.progress, lessonProgress, this.currentTimeToAddSeconds)
      .pipe(finalize(() => {
        if (this.isCurrentLessonSession(saveSessionKey)) {
          this.updatingProgress = false;
          this.flushPendingVideoProgressUpdate();
        }
      }))
      .subscribe(result => {
        if (!this.isCurrentLessonSession(saveSessionKey)) {
          return;
        }

        console.log('Video Progress - Update Complete', furtherestVideoTime, percentComplete, result, event);
        this.progress = result;
        this.lastVideoProgressSaveDateTime = this.progress.currentLessonSession?.SessionEndDateTime || dateTime;
        this.lastSavedFurthestVideoTime = this.progress.currentLessonSession?.VideoFurthestTimeSeconds || furtherestVideoTime || this.lastSavedFurthestVideoTime;
        this.lastVideoStart = this.progress.currentLessonSession?.SessionEndDateTime || dateTime;

        if (isCompletion) {
          if (!this.isVideoCompletionSaved(result)) {
            this.videoCompletionSaveStarted = false;
            return;
          }

          this.videoCompletionSaved = true;
          this.videoSessionStarted = false;
          this.videoPlaybackProgressObserved = false;
          this.pendingVideoProgressUpdate = null;
          this.lastVideoStart = null;
          this.observedVideoPlaybackSeconds = 0;
          this.lastObservedVideoTimeSeconds = null;
          this.lastObservedPlaybackDateTime = null;
        }
      }, error => {
        if (isCompletion) {
          this.videoCompletionSaveStarted = false;
        }

        console.warn('Video Progress - Update Failed', furtherestVideoTime, percentComplete, error, event);
      })
  }

  private handleVideoProgress(videoEvent: VideoEvent) {
    if (!this.progress?.currentLessonSession) {
      return;
    }

    const furtherestVideoTime = videoEvent.furtherestVideoTime || 0;
    const videoTime = videoEvent.videoTime || 0;
    const currentFurthest = this.progress.currentLessonSession.VideoFurthestTimeSeconds || 0;

    this.recordVideoPlaybackProgress(videoEvent);

    if (furtherestVideoTime > currentFurthest) {
      this.videoSessionStarted = true;
      this.videoPlaybackProgressObserved = true;
      this.progress.currentLessonSession.VideoFurthestTimeSeconds = furtherestVideoTime;
    }

    if (videoTime > 0) {
      this.videoSessionStarted = true;
      this.videoPlaybackProgressObserved = true;
    }

    if (this.shouldAutosaveVideoProgress(videoEvent)) {
      this.setVideoProgress(furtherestVideoTime, videoEvent.percentComplete, videoEvent);
    }
  }

  private shouldAutosaveVideoProgress(videoEvent: VideoEvent): boolean {
    const furtherestVideoTime = videoEvent.furtherestVideoTime || 0;

    if (videoEvent.percentComplete >= 100) {
      return true;
    }

    const furthestDelta = furtherestVideoTime - (this.lastSavedFurthestVideoTime || 0);
    if (furthestDelta >= this.videoProgressAutosaveDeltaSeconds) {
      return true;
    }

    if (!this.progress?.lesson?.lessonProgress?.CourseLessonProgressId) {
      return false;
    }

    if (!this.lastVideoProgressSaveDateTime) {
      return false;
    }

    const secondsSinceLastSave = this.dateTimeUtilService.calcDurationSeconds(
      this.lastVideoProgressSaveDateTime,
      this.dateTimeUtilService.getCurrentDateTime(),
      0
    );

    return secondsSinceLastSave >= this.videoProgressAutosaveIntervalSeconds;
  }

  private flushVideoProgress(resetSessionAfterFlush: boolean = false) {
    if (!this.progress?.currentLessonSession || this.lesson?.LessonTypeId !== LessonTypeAllItems.Video) {
      return;
    }

    if (!this.videoSessionStarted || this.videoCompletionSaveStarted || this.videoCompletionSaved) {
      return;
    }

    const furtherestVideoTime = this.progress.currentLessonSession.VideoFurthestTimeSeconds
      || this.progress.lesson?.lessonProgress?.VideoFurthestTimeSeconds
      || 0;
    const percentComplete = this.progress.lesson?.lessonProgress?.PercentComplete || 0;

    if (!furtherestVideoTime && !this.lastVideoStart && this.progress.lesson?.lessonProgress?.CourseLessonProgressId) {
      return;
    }

    this.setVideoProgress(furtherestVideoTime, percentComplete);

    if (resetSessionAfterFlush) {
      this.videoSessionStarted = false;
      this.videoPlaybackProgressObserved = false;
      this.pendingVideoProgressUpdate = null;
      this.lastVideoStart = null;
      this.observedVideoPlaybackSeconds = 0;
      this.lastObservedVideoTimeSeconds = null;
      this.lastObservedPlaybackDateTime = null;
      this.currentTimeToAddSeconds = 0;
    }
  }

  private recordVideoPlaybackProgress(videoEvent?: VideoEvent) {
    const currentVideoTime = videoEvent?.videoTime || videoEvent?.furtherestVideoTime || 0;

    if (!currentVideoTime) {
      return;
    }

    const currentDateTime = this.dateTimeUtilService.getCurrentDateTime();

    if (this.shouldSplitVideoSession(currentDateTime)) {
      this.startNewVideoSessionAfterInactivity(currentDateTime, currentVideoTime);
      return;
    }

    if (videoEvent?.userSeeked) {
      this.lastObservedVideoTimeSeconds = currentVideoTime;
      this.lastObservedPlaybackDateTime = currentDateTime;
      return;
    }

    if (this.lastObservedVideoTimeSeconds == null) {
      this.lastObservedVideoTimeSeconds = currentVideoTime;
      this.lastObservedPlaybackDateTime = currentDateTime;
      return;
    }

    const videoDeltaSeconds = Math.floor(currentVideoTime) - Math.floor(this.lastObservedVideoTimeSeconds);
    this.lastObservedVideoTimeSeconds = currentVideoTime;
    this.lastObservedPlaybackDateTime = currentDateTime;

    if (videoDeltaSeconds <= 0) {
      return;
    }

    this.observedVideoPlaybackSeconds += Math.min(videoDeltaSeconds, this.maxVideoPlaybackDeltaSeconds);
  }

  private shouldSplitVideoSession(currentDateTime: string): boolean {
    if (!this.lastObservedPlaybackDateTime || !this.progress?.currentLessonSession?.SessionStartDateTime) {
      return false;
    }

    const inactiveSeconds = this.dateTimeUtilService.calcDurationSeconds(this.lastObservedPlaybackDateTime, currentDateTime, 0);

    return inactiveSeconds >= this.videoSessionInactivitySplitSeconds;
  }

  private startNewVideoSessionAfterInactivity(currentDateTime: string, currentVideoTime: number) {
    this.progress.currentLessonSession = {
      SessionStartDateTime: currentDateTime,
      SessionEndDateTime: null,
      TotalTimeSeconds: 0,
      VideoId: this.lesson?.Video?.VideoId,
      VideoFurthestTimeSeconds: this.progress.lesson?.lessonProgress?.VideoFurthestTimeSeconds || 0
    };

    this.videoSessionStarted = true;
    this.videoPlaybackProgressObserved = false;
    this.videoCompletionSaveStarted = false;
    this.videoCompletionSaved = false;
    this.pendingVideoProgressUpdate = null;
    this.lastVideoStart = currentDateTime;
    this.lastVideoProgressSaveDateTime = null;
    this.lastSavedFurthestVideoTime = this.progress.lesson?.lessonProgress?.VideoFurthestTimeSeconds || 0;
    this.observedVideoPlaybackSeconds = 0;
    this.currentTimeToAddSeconds = 0;
    this.lastObservedVideoTimeSeconds = currentVideoTime || null;
    this.lastObservedPlaybackDateTime = currentDateTime;
  }

  private flushPendingVideoProgressUpdate() {
    const pendingUpdate = this.pendingVideoProgressUpdate;
    this.pendingVideoProgressUpdate = null;

    if (pendingUpdate) {
      this.setVideoProgress(pendingUpdate.furtherestVideoTime, pendingUpdate.percentComplete, pendingUpdate.event);
    }
  }

  private hasVideoProgressChangesToSave(furtherestVideoTime: number, percentComplete: number): boolean {
    if (percentComplete >= 100) {
      return true;
    }

    return this.videoPlaybackProgressObserved && this.currentTimeToAddSeconds > 0;
  }

  private getLessonSessionKey(): string {
    return [
      this.progress?.lessonReference?.courseId,
      this.progress?.lessonReference?.moduleId,
      this.progress?.lessonReference?.lessonId,
      this.lesson?.CourseLessonId
    ].join('|');
  }

  private isCurrentLessonSession(sessionKey: string): boolean {
    return sessionKey === this.currentLessonSessionKey;
  }

  private isVideoCompletionSaved(progress: LessonProgressHierarchy): boolean {
    const savedLessonProgress = progress?.lesson?.lessonProgress;

    return savedLessonProgress?.LessonComplete === true || (savedLessonProgress?.PercentComplete || 0) >= 100;
  }


  private updateSessionTime(newTotalSeconds?: number) {
    let previousSeconds = this.progress.currentLessonSession.TotalTimeSeconds || 0;

    if (newTotalSeconds !== undefined && newTotalSeconds !== null) {
      this.progress.currentLessonSession.TotalTimeSeconds = newTotalSeconds;
    } else {
      this.progress.currentLessonSession.TotalTimeSeconds = this.dateTimeUtilService.calcDurationSeconds(this.progress.currentLessonSession.SessionStartDateTime, this.progress.currentLessonSession.SessionEndDateTime, 0);
    }

    this.currentTimeToAddSeconds = this.progress.currentLessonSession.TotalTimeSeconds - previousSeconds;
  }


  updateLessonProgress() {

  }

}

